import React, { useState, useEffect } from "react";
import { MDBTextArea, MDBRow, MDBCol } from "mdb-react-ui-kit";
import {
  FormControl,
  FormLabel,
  Select,
  Box,
  Heading,
  Divider,
  Input,
  Flex,
  Text,
  Badge,
  Icon,
  Card,
  CardBody,
  VStack
} from "@chakra-ui/react";
import { FaStore } from "react-icons/fa";
import axios from "axios";
import { API_BASE_URL } from "../config/api_keys";

export default function Details({ onTitleSelect, onDescriptionSelect, onAgricultureDetailsChange, category }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("kg");
  const [cultivationMethod, setCultivationMethod] = useState("");
  const [harvestDate, setHarvestDate] = useState("");
  const [quality, setQuality] = useState("");
  const [variety, setVariety] = useState("");
  const [age, setAge] = useState("");
  const [breed, setBreed] = useState("");
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Fetch user's stores
  useEffect(() => {
    const fetchUserStores = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/my-stores`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        console.log("Fetched stores:", response.data);
        setStores(response.data);
      } catch (error) {
        console.error("Error fetching user stores:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserStores();
  }, []);

  // Determine if livestock fields should be shown
  const isLivestock = category === "Livestock";
  
  // Determine if grains-specific fields should be shown
  const isGrains = category === "Grains";
  
  // Determine appropriate units based on category
  const getUnitOptions = () => {
    if (category === "Grains" || category === "Vegetables" || category === "Spices") {
      return [
        { value: "kg", label: "Kilogram (kg)" },
        { value: "g", label: "Gram (g)" },
        { value: "quintal", label: "Quintal" },
        { value: "ton", label: "Ton" }
      ];
    } else if (category === "Fruits") {
      return [
        { value: "kg", label: "Kilogram (kg)" },
        { value: "g", label: "Gram (g)" },
        { value: "dozen", label: "Dozen" },
        { value: "piece", label: "Piece" }
      ];
    } else if (category === "Dairy") {
      return [
        { value: "liter", label: "Liter" },
        { value: "kg", label: "Kilogram (kg)" },
        { value: "piece", label: "Piece" }
      ];
    } else if (category === "Livestock") {
      return [
        { value: "piece", label: "Piece" },
        { value: "dozen", label: "Dozen" }
      ];
    }
    
    // Default units
    return [
      { value: "kg", label: "Kilogram (kg)" },
      { value: "g", label: "Gram (g)" },
      { value: "quintal", label: "Quintal" },
      { value: "ton", label: "Ton" },
      { value: "piece", label: "Piece" },
      { value: "dozen", label: "Dozen" },
      { value: "liter", label: "Liter" }
    ];
  };

  useEffect(() => {
    // Set default unit based on category when component mounts
    const unitOptions = getUnitOptions();
    if (unitOptions.length > 0) {
      setUnit(unitOptions[0].value);
      
      // Update parent with initial values
      updateParent({
        unit: unitOptions[0].value
      });
    }
    
    // Send initial title and description to parent
    if (onTitleSelect) {
      onTitleSelect({ title: "" });
    }
    
    if (onDescriptionSelect) {
      onDescriptionSelect({ description: "" });
    }
  }, [category]);

  const handleInputChange = (event) => {
    const { id, value } = event.target;
    
    if (id === "title") {
      setTitle(value);
      if (onTitleSelect) {
        onTitleSelect({ title: value });
      }
    }
    
    if (id === "description") {
      setDescription(value);
      if (onDescriptionSelect) {
        onDescriptionSelect({ description: value });
      }
    }
    
    // Handle agriculture-specific fields
    const updates = {};
    
    if (id === "quantity") {
      setQuantity(value);
      updates.quantity = value;
    }
    if (id === "unit") {
      setUnit(value);
      updates.unit = value;
    }
    if (id === "cultivationMethod") {
      setCultivationMethod(value);
      updates.cultivationMethod = value;
    }
    if (id === "harvestDate") {
      setHarvestDate(value);
      updates.harvestDate = value;
    }
    if (id === "quality") {
      setQuality(value);
      updates.quality = value;
    }
    if (id === "variety") {
      setVariety(value);
      updates.variety = value;
    }
    if (id === "age") {
      setAge(value);
      updates.age = value;
    }
    if (id === "breed") {
      setBreed(value);
      updates.breed = value;
    }
    
    updateParent(updates);
  };
  
  const updateParent = (updates) => {
    // Pass all agriculture details to parent
    onAgricultureDetailsChange && onAgricultureDetailsChange({
      quantity: updates.hasOwnProperty('quantity') ? updates.quantity : quantity,
      unit: updates.hasOwnProperty('unit') ? updates.unit : unit,
      cultivationMethod: updates.hasOwnProperty('cultivationMethod') ? updates.cultivationMethod : cultivationMethod,
      harvestDate: updates.hasOwnProperty('harvestDate') ? updates.harvestDate : harvestDate,
      quality: updates.hasOwnProperty('quality') ? updates.quality : quality,
      variety: updates.hasOwnProperty('variety') ? updates.variety : variety,
      age: updates.hasOwnProperty('age') ? updates.age : age,
      breed: updates.hasOwnProperty('breed') ? updates.breed : breed,
      store: updates.hasOwnProperty('store') ? updates.store : selectedStore
    });
  };

  return (
    <Box className="mt-3 mb-1">
      <Box className="mt-3 custom-file-input">
        <FormControl mb={4} isRequired>
          <FormLabel fontWeight="bold">Product Name</FormLabel>
          <Input 
            placeholder="Enter name of your product" 
            id="title" 
            value={title}
            onChange={handleInputChange}
          />
        </FormControl>
        
        <FormControl mb={4} isRequired>
          <FormLabel fontWeight="bold">Description</FormLabel>
          <MDBTextArea 
            placeholder="Provide details about your product" 
            id="description" 
            rows={4} 
            value={description}
            onChange={handleInputChange} 
          />
        </FormControl>
        
        <Heading as="h5" size="sm" fontWeight="bold" mb={3} mt={6}>
          Product Details
        </Heading>
        <Divider mb={4} />
        
        <Flex direction={{ base: "column", md: "row" }} gap={4} mb={4}>
          <FormControl flex="2" isRequired>
            <FormLabel fontWeight="bold">Quantity</FormLabel>
            <Input 
              placeholder="Enter quantity" 
              id="quantity" 
              type="number" 
              value={quantity}
              onChange={handleInputChange} 
            />
          </FormControl>
          
          <FormControl flex="1">
            <FormLabel fontWeight="bold">Unit</FormLabel>
            <Select 
              id="unit" 
              value={unit} 
              onChange={handleInputChange}
            >
              {getUnitOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </Flex>
        
        <Text fontSize="sm" color="gray.500" mb={4}>
          You are listing {quantity || '0'} {unit} of product for sale
        </Text>
        
        <FormControl mb={4}>
          <FormLabel fontWeight="bold">Variety/Type</FormLabel>
          <Input 
            placeholder="e.g., Basmati, Golden Delicious, Jersey" 
            id="variety" 
            value={variety}
            onChange={handleInputChange} 
          />
        </FormControl>
        
        {/* Show cultivation method and harvest date only for Grains category */}
        {isGrains && (
          <>
            <FormControl mb={4}>
              <FormLabel fontWeight="bold">Cultivation Method</FormLabel>
              <Select 
                id="cultivationMethod" 
                value={cultivationMethod}
                onChange={handleInputChange}
                placeholder="Select cultivation method"
              >
                <option value="organic">Organic</option>
                <option value="conventional">Conventional</option>
                <option value="natural">Natural Farming</option>
                <option value="hydroponic">Hydroponic</option>
              </Select>
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel fontWeight="bold">Harvest Date</FormLabel>
              <Input 
                type="date" 
                id="harvestDate" 
                value={harvestDate}
                onChange={handleInputChange} 
              />
            </FormControl>
          </>
        )}
        
        <FormControl mb={4}>
          <FormLabel fontWeight="bold">Quality Grade</FormLabel>
          <Select 
            id="quality" 
            value={quality}
            onChange={handleInputChange}
            placeholder="Select quality grade"
          >
            <option value="A">Grade A (Premium)</option>
            <option value="B">Grade B (Standard)</option>
            <option value="C">Grade C (Economy)</option>
          </Select>
        </FormControl>
        
        {/* Store Selection */}
        <FormControl mb={4}>
          <FormLabel fontWeight="bold">
            <Flex align="center">
              <Icon as={FaStore} mr={2} color="green.500" />
              Associate with Store
              <Badge ml={2} colorScheme="green" variant="outline">Optional</Badge>
            </Flex>
          </FormLabel>
          <Select 
            id="store"
            placeholder="Select a store (optional)"
            value={selectedStore}
            onChange={(e) => {
              setSelectedStore(e.target.value);
              updateParent({store: e.target.value});
              console.log("Selected store:", e.target.value);
            }}
            isDisabled={loading}
          >
            {stores.length > 0 ? (
              stores.map(store => (
                <option key={store._id} value={store._id}>
                  {store.name}
                </option>
              ))
            ) : (
              <option disabled value="">
                No stores available
              </option>
            )}
          </Select>
          {loading ? (
            <Text fontSize="sm" color="gray.500" mt={1}>
              Loading your stores...
            </Text>
          ) : stores.length === 0 ? (
            <Text fontSize="sm" color="gray.500" mt={1}>
              You don't have any stores yet. <a href="/create-store" style={{color: 'green', textDecoration: 'underline'}}>Create a store</a> to associate with your products.
            </Text>
          ) : null}
        </FormControl>
        
        {/* Livestock specific fields - conditionally shown */}
        {isLivestock && (
          <Box mt={4}>
            <Heading as="h5" size="sm" fontWeight="bold" mb={3}>
              Livestock Details
            </Heading>
            <Divider mb={4} />
            
            <FormControl mb={4}>
              <FormLabel fontWeight="bold">Age (months)</FormLabel>
              <Input 
                type="number" 
                id="age" 
                value={age}
                onChange={handleInputChange} 
                placeholder="Enter age in months"
              />
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel fontWeight="bold">Breed</FormLabel>
              <Input 
                id="breed" 
                value={breed}
                onChange={handleInputChange} 
                placeholder="Enter breed name"
              />
            </FormControl>
          </Box>
        )}
      </Box>
    </Box>
  );
}